import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  useNavigate,
  useParams,
  useSearch,
} from '@tanstack/react-router';
import { useCallback } from 'react';
import { AppShell } from './layout/AppShell';
import { DashboardPage } from '@/features/dashboard/DashboardPage';
import { DevicesPage } from '@/features/devices/DevicesPage';
import { DeviceDetailPage } from '@/features/devices/DeviceDetailPage';
import { OrganizationsPage } from '@/features/organizations/OrganizationsPage';
import { SessionsPage } from '@/features/sessions/SessionsPage';
import { TechniciansPage } from '@/features/technicians/TechniciansPage';
import { AuditLogPage } from '@/features/audit/AuditLogPage';
import { AdministrationPage } from '@/features/administration/AdministrationPage';
import { NotFoundPage } from '@/features/NotFoundPage';
import {
  resolveDeviceSearch,
  toUrlSearch,
  validateDeviceSearch,
  type DeviceSearchParams,
} from '@/features/devices/device-search';

/*
  Code-based route tree rather than the file-based generator.

  Reason: the file-based router needs a plugin that writes a generated routeTree.gen.ts into
  the source tree, and that file has to be produced before a typecheck or a build will pass.
  For a Phase 1 surface of seven routes the generator earns nothing and costs a build step
  that a clean checkout must run before anything works. The tree below is the whole routing
  configuration, visible in one file. Revisit if the route count grows past roughly twenty or
  once nested loaders are in play.
*/

const rootRoute = createRootRoute({
  component: AppShell,
  notFoundComponent: NotFoundPage,
});

const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: DashboardPage,
});

/**
 * The devices route owns its filter state in the URL. validateSearch treats the query string
 * as untrusted input and drops anything that is not a known value, so a hand-edited or
 * hostile URL cannot inject a filter value into the API call.
 */
const devicesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/devices',
  validateSearch: validateDeviceSearch,
  component: DevicesRouteComponent,
});

function DevicesRouteComponent(): React.JSX.Element {
  const urlSearch = useSearch({ from: '/devices' });
  const search = resolveDeviceSearch(urlSearch);
  const navigate = useNavigate();

  const onSearchChange = useCallback(
    (patch: Partial<DeviceSearchParams>): void => {
      void navigate({
        to: '/devices',
        search: toUrlSearch({ ...resolveDeviceSearch(urlSearch), ...patch }),
        // Filter changes should not each become a back-button stop. The back button undoes
        // the arrival at the filtered view, not thirty keystrokes.
        replace: true,
      });
    },
    [navigate, urlSearch],
  );

  return <DevicesPage search={search} onSearchChange={onSearchChange} />;
}

/**
 * Device detail, at /devices/{id}.
 *
 * A sibling of /devices rather than a child, because the detail page replaces the list rather
 * than rendering inside it: a nested route would need the list route to render an Outlet and
 * would keep the 25 row query mounted behind a page that does not use it.
 *
 * The id is taken as an opaque path segment and handed straight to the API. It is not
 * validated into a UUID here on purpose: a malformed id and an id from another tenant must
 * both come back as the same 404 from the server (docs/07-identity-rbac.md I.3), and a client
 * side format check would turn one of those two into a different message and start leaking the
 * distinction.
 */
const deviceDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/devices/$deviceId',
  component: DeviceDetailRouteComponent,
});

function DeviceDetailRouteComponent(): React.JSX.Element {
  const { deviceId } = useParams({ from: '/devices/$deviceId' });
  return <DeviceDetailPage deviceId={deviceId} />;
}

const organizationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/organizations',
  component: OrganizationsPage,
});

const sessionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sessions',
  component: SessionsPage,
});

const techniciansRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/technicians',
  component: TechniciansPage,
});

const auditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/audit',
  component: AuditLogPage,
});

const administrationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/administration',
  component: AdministrationPage,
});

const routeTree = rootRoute.addChildren([
  dashboardRoute,
  devicesRoute,
  deviceDetailRoute,
  organizationsRoute,
  sessionsRoute,
  techniciansRoute,
  auditRoute,
  administrationRoute,
]);

export const router = createRouter({
  routeTree,
  defaultNotFoundComponent: NotFoundPage,
  // Scroll restoration matters on the device list: coming back from a device should land
  // where you left, not at the top of 4,000 rows.
  scrollRestoration: true,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

export { Outlet };
